// 登录方式
import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import intl from 'react-intl-universal';

import {
  AccountType,
  LoginMethodEnum,
  EmployeeTypeList,
  SystemTypeList,
} from '@/pages/login/helper';
import { getUserLoginToken } from '@/api/login';
import { getLoginMethodListWithCache } from '@/api/cacheRequest';

import PermissionDeniedForm from '../PermissionDenied';
import DingTalkQRCodeForm from '../DingTalkCodeLogin';
import MobileCodeLogin from '../MobileCodeLogin';
import MobilePasswordLogin from '../MobilePasswordLogin';
import AccountPasswordLogin from '../AccountPasswordLogin';
import TypeSwitch from '../TypeSwitch';
import MethodSwitch from '../MethodSwitch';
import styles from './index.module.scss';

const AccountNameMap = {
  [AccountType.EmployeeAccount]: intl.get('login_account_type_employee'),
  [AccountType.SystemAccount]: intl.get('login_account_type_system'),
};

const LoginFormMap = {
  [LoginMethodEnum.EmployeeDingTalkQRCode]: DingTalkQRCodeForm,
  [LoginMethodEnum.EmployeeMobilePassword]: MobilePasswordLogin,
  [LoginMethodEnum.EmployeeAccountPassword]: AccountPasswordLogin,
  [LoginMethodEnum.EmployeeMobileCode]: MobileCodeLogin,
  [LoginMethodEnum.SystemMobileCode]: MobileCodeLogin,
  [LoginMethodEnum.SystemMobilePassword]: MobilePasswordLogin,
};

const Index = (props) => {
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loginConfig, setLoginConfig] = useState({ employeeMethodList: [], systemMethodList: [] });
  const [loginOption, setLoginOption] = useState({
    switchVisible: false, // 类型切换
    accountType: AccountType.EmployeeAccount, // 类型
    loginMethodId: null, // 登录方式
  });

  // 获取登录方式
  const initLoginMethodList = async () => {
    setLoadingStatus(true);
    try {
      const employeeMethodList = []; // 员工方式
      const systemMethodList = []; // 系统方式
      const res = await getLoginMethodListWithCache();
      const allMethodList = res.data || [];
      const filterMethodList = [];
      allMethodList.forEach((method) => {
        const { accountType, loginType } = method;
        const employeeItem = EmployeeTypeList.find(
          (item) => item.accountType === accountType && item.loginType === loginType,
        );
        if (employeeItem) {
          filterMethodList.push(employeeItem);
          employeeMethodList.push(employeeItem);
        }
        const systemItem = SystemTypeList.find(
          (item) => item.accountType === accountType && item.loginType === loginType,
        );
        if (systemItem) {
          filterMethodList.push(systemItem);
          systemMethodList.push(systemItem);
        }
      });
      setLoginConfig({
        employeeMethodList,
        systemMethodList,
      });
      const defaultMethod = filterMethodList[0];
      if (defaultMethod) {
        setLoginOption({
          switchVisible: !!(employeeMethodList.length && systemMethodList.length),
          accountType: defaultMethod.accountType,
          loginMethodId: defaultMethod.id,
        });
      } else {
        throw new Error();
      }
    } catch (e) {
      setLoginOption({
        ...loginOption,
        loginMethodId: LoginMethodEnum.PermissionDenied,
      });
    }
    setLoadingStatus(false);
  };

  // 切换类型
  const handleToggleType = () => {
    const { accountType } = loginOption;
    const { systemMethodList, employeeMethodList } = loginConfig;
    if (systemMethodList.length && employeeMethodList.length) {
      const isEmployeeType = accountType === AccountType.EmployeeAccount;
      setLoginOption({
        ...loginOption,
        accountType: isEmployeeType ? AccountType.SystemAccount : AccountType.EmployeeAccount,
        loginMethodId: (isEmployeeType ? systemMethodList : employeeMethodList)[0].id,
      });
    }
  };

  // 修改登录方式
  const handleMethodChange = (val) => {
    setLoginOption({
      ...loginOption,
      loginMethodId: val,
    });
  };

  const handleLoginSubmit = async (data) => {
    setLoadingStatus(true);
    try {
      if (document && document.activeElement) {
        // 失焦 防止回车时重复提交
        (document.activeElement as HTMLInputElement).blur();
      }
    } catch (_) {
      //
    }
    try {
      const { accountType, loginMethodId } = loginOption;
      const methodItem = (accountType === AccountType.EmployeeAccount
        ? EmployeeTypeList
        : SystemTypeList
      ).find((item) => item.id === loginMethodId);
      const res = await getUserLoginToken({
        ...data,
        accountType,
        loginType: methodItem ? methodItem.loginType : null,
      });
      if (res && res.data) {
        props.onLoginSuccess(res.data);
      }
    } catch (_) {
      //
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    initLoginMethodList();
  }, []);

  const renderLoginForm = () => {
    const { switchVisible, accountType, loginMethodId } = loginOption;
    if (loginMethodId !== null) {
      const { systemMethodList, employeeMethodList } = loginConfig;
      if (loginMethodId === LoginMethodEnum.PermissionDenied) {
        return <PermissionDeniedForm />;
      }
      const isEmployeeType = accountType === AccountType.EmployeeAccount;
      const LoginForm = LoginFormMap[loginMethodId];
      return (
        <div className={styles.formWrapper}>
          <div className={styles.headerText}>{AccountNameMap[accountType]}</div>
          <div className={styles.formBody}>
            <LoginForm onSubmit={handleLoginSubmit} key={loginMethodId} />
          </div>
          <div className={styles.methodBody}>
            <MethodSwitch
              value={loginMethodId}
              onChange={handleMethodChange}
              options={isEmployeeType ? employeeMethodList : systemMethodList}
            />
          </div>
          <div className={styles.footerText}>{intl.get('login_sms_hint')}</div>
          <TypeSwitch
            text={
              AccountNameMap[
                isEmployeeType ? AccountType.SystemAccount : AccountType.EmployeeAccount
              ]
            }
            visible={switchVisible}
            onToggle={handleToggleType}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles.container}>
      <Spin spinning={loadingStatus}>{renderLoginForm()}</Spin>
    </div>
  );
};

export default Index;
